import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import useCheckoutState from '../hooks/useCheckoutState';
import { get } from 'lodash';

/**
 * Fallback component rendered when StyledElement encounters an error
 */
const StyledElementFallback = ({ error, resetErrorBoundary, fallbackProps }) => {
  const { children, style, as: Component = 'div', className, ...props } = fallbackProps;

  // Render a simple unstyled element with just the passed props
  return (
    <Component className={className} style={style} {...props}>
      {children}
    </Component>
  );
};

/**
 * StyledElement - Wrapper component that applies theme element styles using inline styles
 *
 * @param {string} elementPath - Path to the element in theme.elements (e.g., 'checkout.form.button')
 * @param {object} styleProps - Additional style properties to apply
 * @param {string} as - Element type to render (defaults to div)
 * @param {boolean} checkEnabled - Whether to check if the element is enabled in the theme
 * @param {object} props - Additional props to pass to the element
 */
const StyledElementInner = ({
  elementPath,
  style = {},
  as: Component = 'div',
  checkEnabled = true,
  children,
  className,
  ...props
}) => {
  const { theme } = useCheckoutState();

  // If theme is not available, render with just the passed style props
  if (!theme || !theme.elements) {
    return (
      <Component className={className} style={style} {...props}>
        {children}
      </Component>
    );
  }

  // Check if element is enabled in theme
  const isEnabled = checkEnabled
    ? typeof get(theme.elements, `${elementPath}.enabled`) === 'boolean'
      ? get(theme.elements, `${elementPath}.enabled`)
      : true
    : true;

  // If element is disabled and we're checking for enabled status, don't render
  if (checkEnabled && !isEnabled) {
    return null;
  }

  return (
    <Component className={className} style={style} {...props}>
      {children}
    </Component>
  );
};

// Wrapper component that includes error boundary
const StyledElement = props => {
  return (
    <ErrorBoundary
      fallbackRender={({ error, resetErrorBoundary }) => (
        <StyledElementFallback
          error={error}
          resetErrorBoundary={resetErrorBoundary}
          fallbackProps={props}
        />
      )}
      onError={(error, errorInfo) => {
        console.error('StyledElement Error:', error, errorInfo);
      }}
    >
      <StyledElementInner {...props} />
    </ErrorBoundary>
  );
};

export default StyledElement;
